import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Constellation from "@/components/effects/Constellation";
import Reveal from "@/components/effects/Reveal";
import GoldButton, { GhostButton } from "@/components/effects/GoldButton";
import { Target, Handshake, Megaphone, Check } from "lucide-react";
import "@/styles/landing-mockup.css";
import { usePricing, proPrice, euro } from "@/lib/pricing";

// Landing = CaptAccion_Mockup_Premium.html (vista «Landing»), salvo los 3 cuadros de precios, que se conservan.
// Datos de contacto/legales: solo se muestran si existen (nunca placeholders a la vista).
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "";
const SUPPORT_WHATSAPP = process.env.REACT_APP_SUPPORT_WHATSAPP || "";   // p. ej. https://wa.me/34600000000
const LEGAL_NAME = process.env.REACT_APP_LEGAL_NAME || "";               // p. ej. «Nombre LLC · NIF/EIN»
// Testimonios: diseño del mockup listo pero oculto hasta tener casos reales (rellenar TESTIMONIALS y poner true).
const SHOW_TESTIMONIALS = false;
const TESTIMONIALS = [
  { quote: "Frase corta y concreta de un cliente real, con un resultado medible.", by: "Nombre · Rol · Ciudad" },
  { quote: "Antes / después en una línea.", by: "Nombre · Rol" },
  { quote: "Reuniones agendadas en las primeras semanas.", by: "Enlace al caso completo" },
];

const PERSONAS = [
  { icon: Target, t: "Negocios de servicios", s: "Seguros, energía, formación o consultoría que buscan clientes por redes sin perder horas.", r: "Más clientes que encajan, sin perder horas" },
  { icon: Handshake, t: "Networkers y distribuidores", s: "Encuentra socios, distribuidores y colaboradores afines a tu proyecto, con criterio.", r: "Socios y distribuidores afines, con criterio" },
  { icon: Megaphone, t: "Profesionales que recomiendan", s: "Perfiles complementarios que pueden recomendarte clientes y ampliar tu alcance.", r: "Más recomendaciones y más alcance" },
];

const STATS = [
  { n: 1, txt: "agenda priorizada cada mañana, lista en minutos" },
  { n: 100, suf: "%", metal: true, txt: "de los mensajes los revisas y apruebas tú" },
  { n: 3, txt: "redes en el plan · Instagram activo hoy" },
  { n: 5, txt: "oportunidades de calidad por búsqueda" },
];

const FAQ = [
  ["¿Necesito experiencia en ventas o redes?", "No. CaptAcción está pensado para usarse con o sin experiencia: te dice a quién contactar, qué decir y cuándo hacer seguimiento."],
  ["¿CaptAcción envía mensajes por mí?", "No. Preparamos el mensaje; tú lo revisas, lo copias y lo envías desde tu red. Así tu cuenta sigue siendo tuya y segura."],
  ["¿En qué redes funciona?", "Instagram está activo hoy. Facebook y LinkedIn están en desarrollo."],
  ["¿Puedo cancelar cuando quiera?", "Sí. Sin permanencia: podrás cancelar cuando quieras."],
  ["¿Qué pasa con mis datos?", "Tus datos son tuyos y solo se usan para tu prospección. No los compartimos ni los vendemos. Detalle en la política de privacidad."],
];

const Tick = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M20 6 9 17l-5-5" /></svg>
);

const PLANS = [
  { name: "Gratis", m: 0, y: 0, tag: "Empieza gratis", feats: ["Acceso real a CaptAcción", "3 búsquedas al mes", "CRM y seguimiento", "Instagram"], cta: "Empezar gratis", free: true },
  { name: "Pro", m: 57, y: 570, disc: 17, tag: "El favorito", feats: ["50 búsquedas al mes", "Prospección con IA", "CRM y seguimientos", "Mensajes recomendados", "Instagram"], cta: "Empieza gratis", plan: "pro", highlight: true },
  { name: "Business", m: 197, y: 1970, disc: 17, tag: "Para equipos", feats: ["5 personas incluidas, cada una como un Pro", "Bolsa común: 50 búsquedas por usuario", "Todo el equipo para tu negocio", "Sin prospectos repetidos en el equipo", "Soporte prioritario"], cta: "Empieza gratis", plan: "business" },
];

// Comportamientos del mockup (reveal, cifras, flujo, tilt) portados a React. Respetan «reducir movimiento».
function useMockupEffects(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const FINE = window.matchMedia("(pointer: fine)").matches;
    const cleanups = [];

    // Reveal: lo visible al cargar nunca se oculta.
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
    }), { threshold: 0.15 });
    if (!RM) {
      // v7: se vigilan también los bloques que ya quedaron preparados (is-armed) en una ejecución anterior
      // del efecto (React en modo desarrollo lo ejecuta dos veces); antes se quedaban ocultos para siempre.
      root.querySelectorAll("[data-reveal]:not(.is-in)").forEach((el) => {
        if (!el.classList.contains("is-armed")) {
          const r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) return;
          el.classList.add("is-armed");
        }
        io.observe(el);
      });
    }
    cleanups.push(() => io.disconnect());

    // Cifras con contador + foco.
    const stats = root.querySelector("#stats");
    const countUp = (els) => els.forEach((el) => {
      const to = +el.dataset.count;
      if (RM) { el.textContent = to.toLocaleString("es-ES"); return; }
      const t0 = performance.now(), dur = 1400;
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / dur), e = k === 1 ? 1 : 1 - Math.pow(2, -10 * k);
        el.textContent = Math.round(to * e).toLocaleString("es-ES");
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
    if (stats) {
      const so = new IntersectionObserver((es, o) => es.forEach((e) => {
        if (e.isIntersecting) { stats.classList.add("in"); countUp(stats.querySelectorAll("[data-count]")); o.disconnect(); }
      }), { threshold: 0.4 });
      so.observe(stats);
      const over = (e) => { if (e.pointerType === "mouse" && e.target.closest(".stat")) stats.classList.add("has-focus"); };
      const leave = () => stats.classList.remove("has-focus");
      stats.addEventListener("pointerover", over);
      stats.addEventListener("pointerleave", leave);
      cleanups.push(() => { so.disconnect(); stats.removeEventListener("pointerover", over); stats.removeEventListener("pointerleave", leave); });
    }

    // Diagrama de flujo que se dibuja y luz que corre.
    const flow = root.querySelector("#flow");
    if (flow) {
      let tmr;
      const fo = new IntersectionObserver((es, o) => es.forEach((e) => {
        if (e.isIntersecting) { flow.classList.add("in"); tmr = setTimeout(() => flow.classList.add("flowing"), 1700); o.disconnect(); }
      }), { threshold: 0.45 });
      fo.observe(flow);
      cleanups.push(() => { fo.disconnect(); clearTimeout(tmr); });
    }

    // Tilt 3D y producto del hero que se «endereza» al hacer scroll.
    if (FINE && !RM) {
      root.querySelectorAll("[data-tilt]").forEach((el) => {
        const max = +el.dataset.tilt; const base = el.id === "product" ? 14 : 0; let raf;
        const move = (e) => {
          if (e.pointerType !== "mouse") return;
          const r = el.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            el.classList.add("is-tilting");
            el.style.transform = `perspective(900px) rotateX(${base * (el.id === "product" ? 0.35 : 0) - y * max * 2}deg) rotateY(${x * max * 2}deg)`;
          });
        };
        const leave = () => { el.classList.remove("is-tilting"); el.style.transform = ""; };
        el.addEventListener("pointermove", move);
        el.addEventListener("pointerleave", leave);
        cleanups.push(() => { el.removeEventListener("pointermove", move); el.removeEventListener("pointerleave", leave); });
      });
    }
    const prod = root.querySelector("#product");
    if (prod && !RM) {
      const onScroll = () => {
        if (prod.classList.contains("is-tilting")) return;
        const k = Math.max(0, Math.min(1, window.scrollY / 420));
        prod.style.transform = `rotateX(${14 * (1 - k)}deg)`;
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
    }
    return () => cleanups.forEach((f) => f());
  }, [rootRef]);
}

export default function Landing() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [annual, setAnnual] = useState(false);
  const pricing = usePricing();
  // v11: precios y oferta desde el panel admin (si no responde, los de siempre).
  const plans = PLANS.map((pl) => (pl.name === "Pro"
    ? { ...pl, m: pricing.pro.monthly, y: pricing.pro.yearly, disc: pricing.pro.yearly_discount }
    : pl.name === "Business"
      ? { ...pl, m: pricing.business.monthly, y: pricing.business.yearly, disc: pricing.business.yearly_discount,
          feats: pricing.business.extra_user_monthly
            ? [...pl.feats, `Usuario extra: ${euro(pricing.business.extra_user_monthly)} €/mes${pricing.business.extra_user_yearly ? ` o ${euro(pricing.business.extra_user_yearly)} €/año` : ""}`]
            : pl.feats }
      : pl));
  const [demoLoading, setDemoLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useMockupEffects(rootRef);

  const tryDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    const res = await login("demo@captaccion.app", "captaccion2026");
    setDemoLoading(false);
    if (res.ok) navigate("/app/hoy"); else navigate("/acceso");
  };
  const demoLabel = (t) => (demoLoading ? "Entrando…" : t);

  return (
    <div className="lp" ref={rootRef}>
      <header className="nav">
        <div className="wrap bar glass">
          <Link to="/" className="logo" aria-label="CaptAcción, inicio">
            <img className="logo-img" src="/logo-captaccion-h96.webp" alt="CaptAcción" />
          </Link>
          <ul>
            <li><a href="#como">Cómo funciona</a></li>
            <li><a href="#para">Para quién</a></li>
            <li><a href="#precio">Precio</a></li>
            <li><a href="#faq">Preguntas</a></li>
          </ul>
          <div className="acts">
            <Link className="btn btn-ghost btn-sm" to="/acceso" data-testid="nav-login">Entrar</Link>
            <Link className="btn btn-gold btn-sm" to="/registro" data-testid="nav-register">Crear mi cuenta</Link>
            <button className="burger" aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"} aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}><span></span><span></span></button>
          </div>
          {menuOpen && (
            <nav className="mnav glass" onClick={() => setMenuOpen(false)}>
              <a href="#como">Cómo funciona</a><a href="#para">Para quién</a><a href="#precio">Precio</a><a href="#faq">Preguntas</a>
              <Link to="/acceso">Entrar</Link><Link to="/registro">Crear mi cuenta</Link>
            </nav>
          )}
        </div>
      </header>

      <section className="hero aurora">
        <Constellation />
        <div className="grid-fade" aria-hidden="true"></div>
        <div className="wrap">
          <div className="hero-copy">
            <span className="pill glass"><em>NUEVO</em> Instagram ya disponible · Facebook y LinkedIn en camino</span>
            <h1>Consigue clientes en redes <span className="serif text-metal mask-word" data-texto="sin improvisar">sin improvisar</span></h1>
            <p className="lead">CaptAcción te dice <strong>a quién contactar</strong>, <strong>qué decirle</strong> y <strong>cuándo volver a escribir</strong>. Cada mañana, una agenda priorizada con mensajes en tu tono, listos para revisar y enviar.</p>
            <div className="ctas">
              <Link className="btn btn-gold cta-pulse" to="/registro" data-testid="hero-register">Crear mi cuenta <span className="arr">→</span></Link>
              <button className="btn btn-ghost" onClick={tryDemo} disabled={demoLoading} data-testid="hero-demo">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                {demoLabel("Probar la demo en vivo")}
              </button>
            </div>
            <div className="trust">
              <span><Tick />Tú apruebas cada mensaje</span>
              <span><Tick />Sin envíos automáticos</span>
              <span><Tick />Cancelas cuando quieras</span>
            </div>
          </div>
          {/* Producto real en HTML (no imagen) */}
          <div className="stage">
            <div className="product glass" id="product" data-tilt="5">
              <div className="glow"></div>
              <div className="app">
                <aside>
                  <span className="logo">Capt<i></i><b>Acción</b></span>
                  <span className="nav-i on">◎ Qué hacer hoy <span className="n">4</span></span>
                  <span className="nav-i">◇ CRM y contactos</span>
                  <span className="nav-i">✦ Oportunidades <span className="n">3</span></span>
                  <span className="nav-i">◌ Mi perfil en redes</span>
                  <span className="nav-i">▣ Mi negocio</span>
                  <span className="nav-i">▤ Métricas</span>
                </aside>
                <main>
                  <div className="app-top">
                    <div><small>SÁBADO · 3 OCTUBRE</small><h3>Buenos días, Ernesto</h3></div>
                    <div className="ring" style={{ "--p": 25 }}><span>1/4</span></div>
                  </div>
                  <div className="tasks">
                    <div className="task">
                      <div className="who"><span className="av">LG</span><div><b>Laura Giménez</b><small>Estudio de interiorismo</small></div><span className="score">92</span></div>
                      <div className="msg">Laura, me encantó cómo resolviste el salón de Chamberí, sobre todo la luz. ¿Te cuento en un audio de 2 min una idea para tus presupuestos?</div>
                      <div className="task-acts"><span className="mini g">Copiar y abrir</span><span className="mini o">Hecho</span><span className="chip" style={{ marginLeft: "auto" }}><span className="net-ic ig">◎</span>Audio</span></div>
                    </div>
                    <div className="task">
                      <div className="who"><span className="av">CF</span><div><b>Carlos Ferrer</b><small>Ferrer Consulting</small></div><span className="score">88</span></div>
                      <div className="msg">Carlos, seguimos a la misma gente pero contamos cosas distintas. ¿Hacemos un directo de 30 min juntos esta semana?</div>
                      <div className="task-acts"><span className="mini g">Copiar y abrir</span><span className="mini o">Hecho</span><span className="chip" style={{ marginLeft: "auto" }}><span className="net-ic li">in</span>Mensaje</span></div>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cifras */}
      <div className="wrap">
        <div className="stats" id="stats" data-reveal>
          {STATS.map((s, i) => (
            <div className="stat" style={{ "--i": i }} key={s.txt}>
              <div className={`num${s.metal ? " text-metal" : ""}`}><span data-count={s.n}>{s.n}</span>{s.suf || ""}</div>
              <div className="rule"></div>
              <p>{s.txt}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cómo funciona */}
      <section className="sec" id="como">
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <span className="eyebrow">Cómo funciona</span>
            <h2>Tres pasos y sabes <span className="serif text-metal">exactamente</span> qué hacer</h2>
            <p className="lead">Nada de teoría. Explicas tu negocio, preparamos tus perfiles y cada día recibes acciones concretas.</p>
          </div>
          <div className="flow-wrap" data-reveal="zoom">
            <div className="flow" id="flow">
              <svg viewBox="0 0 1000 220" role="img" aria-label="Flujo: Mi negocio, luego Mapa y auditoría, luego Qué hacer hoy">
                <defs>
                  <linearGradient id="wireG" gradientUnits="userSpaceOnUse" x1="250" y1="0" x2="750" y2="0"><stop offset="0" stopColor="#A47A36" /><stop offset=".5" stopColor="#F7E3B3" /><stop offset="1" stopColor="#A47A36" /></linearGradient>
                  <filter id="glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                </defs>
                <path className="wire" style={{ "--i": 0 }} pathLength="1" d="M270 110 C 320 110, 330 110, 370 110" />
                <path className="wire" style={{ "--i": 1 }} pathLength="1" d="M630 110 C 680 110, 690 110, 730 110" />
                <circle className="spark" r="4" style={{ "--i": 0, offsetPath: "path('M270 110 C 320 110, 330 110, 370 110')" }} />
                <circle className="spark" r="4" style={{ "--i": 1, offsetPath: "path('M630 110 C 680 110, 690 110, 730 110')" }} />
                <g className="node" style={{ "--n": 0 }}><rect x="10" y="50" width="260" height="120" rx="18" /><text className="k" x="34" y="86">01 · ENTRADA</text><text x="34" y="118">Mi negocio</text><text className="s" x="34" y="144">Oferta, avatares y objetivos</text></g>
                <g className="node" style={{ "--n": 1 }}><rect x="370" y="50" width="260" height="120" rx="18" /><text className="k" x="394" y="86">02 · ANÁLISIS</text><text x="394" y="118">Mapa y auditoría</text><text className="s" x="394" y="144">Perfil revisado y canales</text></g>
                <g className="node" style={{ "--n": 2 }}><rect x="730" y="50" width="260" height="120" rx="18" /><text className="k" x="754" y="86">03 · ACCIÓN</text><text x="754" y="118">Qué hacer hoy</text><text className="s" x="754" y="144">Mensajes listos y seguimientos</text></g>
              </svg>
            </div>
            <div className="steps">
              <div className="step" data-reveal style={{ "--d": ".05s" }}><span className="k">PASO 01</span><h3>Cuéntanos tu negocio</h3><p>Qué ofreces y a quién. Definimos tus avatares aunque aún no tengas claro tu cliente ideal.</p><div className="tags"><span className="chip">Oferta</span><span className="chip">Avatares</span><span className="chip">Objetivos</span></div></div>
              <div className="step" data-reveal style={{ "--d": ".15s" }}><span className="k">PASO 02</span><h3>Auditamos tu perfil</h3><p>Te decimos qué cambiar en tu perfil y dónde están tus prospectos en cada red.</p><div className="tags"><span className="chip">Auditoría</span><span className="chip">Canales</span></div></div>
              <div className="step" data-reveal style={{ "--d": ".25s" }}><span className="k">PASO 03</span><h3>Ejecuta tu día</h3><p>Una agenda con mensajes para copiar, editar y enviar. Tú decides qué sale y qué no.</p><div className="tags"><span className="chip">Mensajes</span><span className="chip">Seguimientos</span></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferencia */}
      <section className="sec" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="sec-head center" data-reveal>
            <span className="eyebrow">La diferencia</span>
            <h2>De improvisar cada día<br />a tener <span className="serif text-metal">un sistema</span></h2>
          </div>
          <div className="compare">
            <div className="cmp" data-reveal><span className="tag-k">SIN CAPTACCIÓN</span><h3>Prospección a ciegas</h3>
              <ul><li><i>✕</i>Abres la red sin saber a quién escribir</li><li><i>✕</i>Mensajes copiados que suenan a plantilla</li><li><i>✕</i>Seguimientos que se te olvidan</li><li><i>✕</i>No sabes qué funciona y qué no</li></ul></div>
            <div className="cmp yes" data-reveal style={{ "--d": ".12s" }}><span className="tag-k">CON CAPTACCIÓN</span><h3>Una lista clara cada mañana</h3>
              <ul><li><i>✓</i>Perfiles priorizados por encaje con tu oferta</li><li><i>✓</i>Mensajes personalizados y en tu tono</li><li><i>✓</i>Seguimientos en el momento justo</li><li><i>✓</i>Métricas y mapa de conversión semanal</li></ul></div>
          </div>
        </div>
      </section>

      {/* Para quién */}
      <section className="sec" id="para" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <span className="eyebrow">Para quién es</span>
            <h2>Hecho para quien vende <span className="serif text-metal">por confianza</span></h2>
          </div>
          <div className="personas">
            {PERSONAS.map((p, i) => (
              <article className="persona" data-tilt="6" data-reveal style={{ "--d": `${i * 0.1}s` }} key={p.t}>
                <div className="ico"><p.icon size={24} strokeWidth={1.6} /></div>
                <h3>{p.t}</h3><p>{p.s}</p>
                <div className="res"><b>→</b> {p.r}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Prueba social: oculta hasta tener testimonios reales */}
      {SHOW_TESTIMONIALS && (
        <section className="sec" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="sec-head" data-reveal>
              <span className="eyebrow">Resultados</span>
              <h2>Lo que cuentan <span className="serif text-metal">quienes ya lo usan</span></h2>
            </div>
            <div className="proof">
              {TESTIMONIALS.map((t, i) => (
                <div className="quote" data-reveal style={{ "--d": `${i * 0.1}s` }} key={i}>
                  <blockquote>“{t.quote}”</blockquote><div className="by"><span className="av">··</span>{t.by}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRECIO */}
      <section className="sec" id="precio" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <Reveal variant="zoom">
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <span className="ca-eyebrow">Precio</span>
              <h2 style={{ fontSize: "clamp(28px,4vw,42px)", letterSpacing: "-.02em", margin: "12px 0 20px", fontWeight: 600 }}>
                Elige tu plan
              </h2>
              <div style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 999, border: "1px solid var(--ca-line-2)", background: "var(--ca-surface)" }}>
                {[["Mensual", false], ["Anual", true]].map(([t, v]) => (
                  <button key={t} onClick={() => setAnnual(v)}
                    data-testid={`price-toggle-${v ? "annual" : "monthly"}`}
                    style={{ border: 0, borderRadius: 999, padding: "8px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer",
                      background: annual === v ? "var(--ca-metal)" : "transparent", color: annual === v ? "var(--ca-gold-ink)" : "var(--ca-muted)" }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {plans.map((pl, i) => {
              const offerP = pl.name === "Pro" ? proPrice(pricing, annual) : null;
              const price = offerP ? offerP.now : (annual ? pl.y : pl.m);
              const unit = pl.m === 0 ? "" : annual ? " €/año" : " €/mes";
              const fmt = (n) => n.toLocaleString("es-ES", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
              return (
                <Reveal key={pl.name} delay={i * 0.08}>
                  <div className="ca-tilt-card" style={{ height: "100%", display: "flex", flexDirection: "column", borderColor: pl.highlight ? "rgba(224,185,112,.5)" : undefined }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <h3 style={{ fontSize: 20, fontWeight: 600 }}>{pl.name}</h3>
                      <span style={{ fontFamily: "var(--ca-f-mono)", fontSize: 10, letterSpacing: ".1em", color: "var(--ca-gold)", border: "1px solid var(--ca-line-2)", borderRadius: 999, padding: "3px 8px" }}>{pl.tag}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", fontSize: 40, fontWeight: 600, letterSpacing: "-.03em", marginBottom: 2 }}>
                      {offerP?.before ? (
                        <span data-testid="price-before-pro" style={{ fontSize: 20, color: "var(--ca-subtle)", textDecoration: "line-through", fontWeight: 400 }}>{fmt(offerP.before)} €</span>
                      ) : null}
                      {pl.m === 0 ? "Gratis" : <>{fmt(price)}<span style={{ fontSize: 15, color: "var(--ca-muted)", fontWeight: 400 }}>{unit}</span></>}
                      {offerP?.before ? null : annual && pl.disc ? (
                        <span data-testid={`price-discount-${pl.name.toLowerCase()}`} style={{ fontFamily: "var(--ca-f-mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".06em", color: "var(--ca-gold-ink)", background: "var(--ca-metal)", borderRadius: 999, padding: "3px 9px" }}>-{pl.disc}%</span>
                      ) : null}
                    </div>
                    {offerP?.before && pricing.offer ? (
                      <p data-testid="price-offer-pro" style={{ margin: "6px 0 0", fontSize: 13, color: "var(--ca-gold)", fontWeight: 600 }}>
                        {pricing.offer.label}{pricing.offer.ends_label ? ` · ${pricing.offer.ends_label.toLowerCase()}` : ""}
                      </p>
                    ) : null}
                    <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 24px", display: "grid", gap: 10 }}>
                      {pl.feats.map((f) => (
                        <li key={f} style={{ display: "flex", gap: 10, alignItems: "flex-start", color: "var(--ca-muted)", fontSize: 14 }}>
                          <Check size={16} style={{ color: "var(--ca-gold)", flex: "none", marginTop: 2 }} />{f}
                        </li>
                      ))}
                    </ul>
                    <div style={{ marginTop: "auto" }}>
                      {pl.free
                        ? <GhostButton block as={Link} to="/registro">{pl.cta}</GhostButton>
                        : <GoldButton block arrow={false} as={Link} to={`/registro?plan=${pl.plan}`} data-testid={`plan-cta-${pl.plan}`}>{pl.cta}</GoldButton>}
                      {/* v22: sin pasarela de pago todavía, Pro y Business empiezan en Gratis; se avisa para que no haya sorpresas. */}
                      {!pl.free && (
                        <p data-testid={`plan-note-${pl.plan}`} style={{ margin: "10px 0 0", fontSize: 12.5, lineHeight: 1.45, color: "var(--ca-muted)", textAlign: "center" }}>
                          Pago con tarjeta muy pronto. Empieza gratis y te avisamos cuando puedas pasarte a {pl.name}.
                        </p>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sec" id="faq" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="sec-head center" data-reveal><span className="eyebrow">Preguntas frecuentes</span><h2>Lo que suelen <span className="serif text-metal">preguntarnos</span></h2></div>
          <div className="faq" data-reveal>
            {FAQ.map(([q, a], i) => (
              <details key={q} open={i === 0}><summary>{q}<i>+</i></summary><p>{a}</p></details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final con radar */}
      <section className="wrap">
        <div className="final" data-reveal="zoom">
          <div>
            <span className="eyebrow">Empieza hoy</span>
            <h2 style={{ margin: "18px 0" }}>Convierte conversaciones <span className="serif text-metal">en clientes</span></h2>
            <p className="lead">Mañana a primera hora tendrás tu primera agenda: a quién escribir, con qué mensaje y por qué.</p>
            <div className="ctas" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
              <Link className="btn btn-gold cta-pulse" to="/registro">Crear mi cuenta <span className="arr">→</span></Link>
              <button className="btn btn-ghost" onClick={tryDemo} disabled={demoLoading}>{demoLabel("Ver la demo")}</button>
            </div>
          </div>
          <svg className="radar" viewBox="0 0 520 520" role="img" aria-label="Radar detectando oportunidades">
            <defs>
              <radialGradient id="rh"><stop offset="0" stopColor="#E0B970" stopOpacity=".18" /><stop offset="1" stopColor="#E0B970" stopOpacity="0" /></radialGradient>
              <linearGradient id="rm" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FFF4D6" /><stop offset=".4" stopColor="#D9AE5F" /><stop offset=".7" stopColor="#A97B35" /><stop offset="1" stopColor="#F2D493" /></linearGradient>
              <linearGradient id="sw" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#E0B970" stopOpacity="0" /><stop offset="1" stopColor="#E0B970" stopOpacity=".45" /></linearGradient>
              <filter id="bloom" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="7" result="a" /><feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b" /><feMerge><feMergeNode in="a" /><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
              <filter id="grain"><feTurbulence type="fractalNoise" baseFrequency=".85" numOctaves="2" /><feColorMatrix type="saturate" values="0" /></filter>
            </defs>
            <circle cx="260" cy="260" r="240" fill="url(#rh)" />
            <g fill="none" stroke="#A47A36" strokeOpacity=".4">
              <circle cx="260" cy="260" r="58" /><circle cx="260" cy="260" r="104" /><circle cx="260" cy="260" r="150" /><circle cx="260" cy="260" r="196" />
              <path d="M260 64V456M64 260H456M121 121 399 399M399 121 121 399" />
            </g>
            <circle cx="260" cy="260" r="236" fill="none" stroke="url(#rm)" strokeWidth="2" filter="url(#bloom)" />
            <path className="sweep" d="M260 260 L456 260 A196 196 0 0 0 398 121 Z" fill="url(#sw)" />
            <g fill="#F7E3B3" filter="url(#bloom)">
              <circle className="blip" cx="352" cy="178" r="5" style={{ animationDelay: "-1.2s" }} />
              <circle className="blip" cx="190" cy="140" r="4" style={{ animationDelay: "-3.1s" }} />
              <circle className="blip" cx="150" cy="330" r="5" style={{ animationDelay: "-4.8s" }} />
              <circle className="blip" cx="330" cy="370" r="4" style={{ animationDelay: "-6.2s" }} />
              <circle className="blip" cx="402" cy="290" r="3.5" style={{ animationDelay: "-.4s" }} />
            </g>
            <circle cx="260" cy="260" r="6" fill="#F7E3B3" filter="url(#bloom)" />
            <circle cx="260" cy="260" r="236" filter="url(#grain)" opacity=".05" />
          </svg>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="foot">
            <div><img className="logo-img" src="/logo-captaccion-h96.webp" alt="CaptAcción" /><p style={{ marginTop: 14 }}>Prospección orgánica con acompañamiento. Tus datos son tuyos y solo se usan para tu prospección.</p></div>
            <div><h4>Producto</h4><ul><li><a href="#como">Cómo funciona</a></li><li><a href="#precio">Precio</a></li><li><button type="button" onClick={tryDemo} style={{ padding: 0 }}>Demo en vivo</button></li></ul></div>
            <div><h4>Ayuda</h4><ul>
              <li><a href="#faq">Preguntas</a></li>
              {SUPPORT_EMAIL && <li><a href={`mailto:${SUPPORT_EMAIL}`} data-testid="footer-support">{SUPPORT_EMAIL}</a></li>}
              {SUPPORT_WHATSAPP && <li><a href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">WhatsApp de soporte</a></li>}
            </ul></div>
            <div><h4>Legal</h4><ul><li><Link to="/aviso-legal">Aviso legal</Link></li><li><Link to="/privacidad">Privacidad</Link></li><li><Link to="/cookies">Cookies</Link></li><li><Link to="/terminos">Términos</Link></li></ul></div>
          </div>
          <div className="legal"><span>© {new Date().getFullYear()} CaptAcción{LEGAL_NAME ? ` · ${LEGAL_NAME}` : ""}</span><span>Hecho para quien vende por confianza</span></div>
        </div>
      </footer>
    </div>
  );
}
